import { Stack, DetailsList, DetailsListLayoutMode, IColumn, SelectionMode, Text } from "@fluentui/react";
import { ApiCost } from "../../../api/admin/models_admin";
import { CellContent } from "../components";

// OpenAI 互換 API（/<会社キー>/v1）の利用料の表。管理画面とスーパーユーザー画面のコスト照会で使う

type ApiCostRow = {
    key: string;
    model: string;
    unitName: string;
    token: number;
    cost: number;
};

// 単位ごとの (表示名, トークン数のキー, 金額のキー)
const API_COST_UNITS: [string, keyof ApiCost, keyof ApiCost][] = [
    ["入力", "input_token", "input_cost"],
    ["キャッシュ入力", "cached_input_token", "cached_input_cost"],
    ["キャッシュ書き込み", "cache_write_token", "cache_write_cost"],
    ["出力", "output_token", "output_cost"],
];

// モデル名。長いコンテキストなどの区分は、名前に境目を付けて分ける
const modelLabel = (apiCost: ApiCost): string =>
    apiCost.min_input_token > 0
        ? `${apiCost.model}（入力 ${apiCost.min_input_token.toLocaleString('ja-JP')} トークン超）`
        : apiCost.model;

// モデル × 区分 × 単位の行にする（トークン数が 0 の単位は出さない）
const toRows = (apiCosts: ApiCost[]): ApiCostRow[] =>
    apiCosts.flatMap(apiCost =>
        API_COST_UNITS
            .filter(([, tokenKey]) => Number(apiCost[tokenKey]) > 0)
            .map(([unitName, tokenKey, costKey]) => ({
                key: `${apiCost.model}_${apiCost.min_input_token}_${tokenKey}`,
                model: modelLabel(apiCost),
                unitName,
                token: Number(apiCost[tokenKey]),
                cost: Number(apiCost[costKey]),
            }))
    );

// 複数の会社の利用料を、モデル×区分ごとに合算する
export const mergeApiCosts = (apiCostsList: ApiCost[][]): ApiCost[] => {
    const merged: { [key: string]: ApiCost } = {};
    apiCostsList.forEach(apiCosts => {
        apiCosts.forEach(apiCost => {
            const key = `${apiCost.model}_${apiCost.min_input_token}`;
            const current = merged[key];
            if (!current) {
                merged[key] = { ...apiCost };
                return;
            }
            current.input_token += apiCost.input_token;
            current.input_cost += apiCost.input_cost;
            current.cached_input_token += apiCost.cached_input_token;
            current.cached_input_cost += apiCost.cached_input_cost;
            current.cache_write_token += apiCost.cache_write_token;
            current.cache_write_cost += apiCost.cache_write_cost;
            current.output_token += apiCost.output_token;
            current.output_cost += apiCost.output_cost;
            current.total_cost += apiCost.total_cost;
        });
    });
    return Object.values(merged);
};

const numberStyle = { textAlign: 'right' as const, fontSize: "11pt", fontWeight: 400, color: "black" };

const columns: IColumn[] = [
    {
        key: 'model',
        name: 'モデル名',
        minWidth: 150,
        maxWidth: 250,
        isResizable: true,
        onRender: (row: ApiCostRow) => <CellContent>{row.model}</CellContent>
    },
    {
        key: 'unit',
        name: '区分',
        minWidth: 80,
        maxWidth: 140,
        isResizable: true,
        onRender: (row: ApiCostRow) => <CellContent>{row.unitName}</CellContent>
    },
    {
        key: 'token',
        name: 'トークン数',
        minWidth: 100,
        maxWidth: 150,
        isResizable: true,
        onRender: (row: ApiCostRow) => <div style={numberStyle}>{row.token.toLocaleString('ja-JP')}</div>
    },
    {
        // 単価は月の途中で変わることがあるので、金額 ÷ トークン数で出す
        key: 'unit_cost',
        name: '単価(円)\n(1,000 トークンあたり)',
        minWidth: 120,
        maxWidth: 180,
        isResizable: true,
        isMultiline: true,
        onRender: (row: ApiCostRow) => <div style={numberStyle}>{(row.cost / row.token * 1000).toFixed(3)}</div>
    },
    {
        key: 'cost',
        name: '金額(円)',
        minWidth: 100,
        maxWidth: 150,
        isResizable: true,
        onRender: (row: ApiCostRow) => <div style={numberStyle}>{row.cost.toFixed(2)}</div>
    }
];

type Props = {
    apiCosts: ApiCost[] | undefined;
};

// API の利用が無い月は表示しない
export const ApiCostList = ({ apiCosts }: Props) => {
    const rows = toRows(apiCosts ?? []);
    if (rows.length === 0) {
        return null;
    }

    return (
        <Stack tokens={{ childrenGap: 4 }}>
            <Text variant="large" block>API 利用料（外部のツールからの利用）</Text>
            <DetailsList
                items={rows}
                columns={columns}
                layoutMode={DetailsListLayoutMode.justified}
                selectionMode={SelectionMode.none}
                isHeaderVisible={true}
            />
        </Stack>
    );
};
